import {
  Box,
  Button,
  Group,
  Modal,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconLayoutGridAdd, IconSearch, IconSearchOff } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHomeBlockStore } from "../../store/useHomeBlockStore";
import {
  ACCENT,
  designImage,
  HomeBlockTemplate,
  TEMPLATES,
  templateInfo,
} from "./homeBlockData";
import classes from "./homeBlock.module.css";

type Props = {
  opened: boolean;
  onClose: () => void;
};

const AddBlockModal = ({ opened, onClose }: Props) => {
  const navigate = useNavigate();
  const blocks = useHomeBlockStore((s) => s.blocks);
  const addBlock = useHomeBlockStore((s) => s.addBlock);
  const saving = useHomeBlockStore((s) => s.saving);

  const [picked, setPicked] = useState<HomeBlockTemplate>("product");
  const [query, setQuery] = useState("");
  const [name, setName] = useState(templateInfo("product").defaultName);
  const [position, setPosition] = useState<string | null>(null);
  const [state, setState] = useState("hidden");

  const positionOptions = useMemo(
    () => [
      { value: "0", label: "Đầu danh sách (vị trí 01)" },
      ...blocks.map((b, i) => ({
        value: String(i + 1),
        label: `Sau ${String(i + 1).padStart(2, "0")} · ${b.name}`,
      })),
    ],
    [blocks]
  );

  useEffect(() => {
    if (!opened) return;
    const monitor = blocks.findIndex((b) => b.code === "home-monitor");
    setPosition(String(monitor >= 0 ? monitor + 1 : blocks.length));
    setQuery("");
    setState("hidden");
    // Chỉ đặt lại khi mở modal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const pick = (key: HomeBlockTemplate) => {
    setPicked(key);
    setName(templateInfo(key).defaultName);
  };

  const list = TEMPLATES.filter((t) => {
    const q = query.trim().toLowerCase();
    return !q || `${t.name} ${t.desc}`.toLowerCase().includes(q);
  });
  const current = templateInfo(picked);

  const handleAdd = async () => {
    const created = await addBlock(
      picked,
      name,
      Number(position ?? blocks.length),
      state === "visible"
    );
    if (!created) return;
    onClose();
    navigate(`/fixed-content/home-block/${created.id}`);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group component="span" gap={12} wrap="nowrap">
          <span className={classes.addModalIcon} aria-hidden>
            <IconLayoutGridAdd size={20} stroke={1.75} />
          </span>
          <span>
            <Text component="span" display="block" fz={17} fw={700} lh={1.3}>
              Thêm block trang chủ
            </Text>
            <Text component="span" display="block" fz={13} lh={1.4} opacity={0.85}>
              Chọn một mẫu, đặt vị trí trên trang rồi sửa nội dung
            </Text>
          </span>
        </Group>
      }
      padding={0}
      styles={{
        // padding={0} cho thân modal sát mép; tiêu đề vẫn cần lề riêng
        header: { padding: "14px 16px 14px 20px" },
        // index.css đặt flex: none cho mọi modal nên modal co theo nội dung. Lưới mẫu đầy thì
        // modal rộng hết khung (trừ lề 5vw); giữ đúng bề rộng đó cả khi tìm không ra mẫu nào
        content: { width: "100%" },
      }}
      centered
    >
      <div className={`${classes.page} ${classes.addModalBody}`}>
        <Stack gap={14} p={20} className={classes.addModalList}>
          <TextInput
            aria-label="Tìm mẫu block"
            placeholder="Tìm mẫu: sản phẩm, banner, video..."
            leftSection={<IconSearch size={16} />}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            maw={360}
          />
          <SimpleGrid
            role="radiogroup"
            aria-label="Mẫu block"
            cols={{ base: 1, xs: 2, md: 3, lg: 4 }}
            spacing={12}
          >
            {list.map((t) => {
              const active = t.key === picked;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`${classes.tplCard} ${active ? classes.tplActive : ""}`}
                  onClick={() => pick(t.key)}
                >
                  <img
                    src={designImage(t.thumbId, 480)}
                    alt=""
                    referrerPolicy="no-referrer"
                    loading="lazy"
                  />
                  <Stack gap={4} p="10px 12px 12px">
                    <Text fz={14} fw={600} lh={1.3}>
                      {t.name}
                    </Text>
                    <Text fz={13} lh={1.4} c="var(--hb-ink-2)">
                      {t.desc}
                    </Text>
                    <Text fz={12} lh={1.4} className={classes.muted}>
                      Đang dùng: {t.used}
                    </Text>
                  </Stack>
                </button>
              );
            })}
          </SimpleGrid>
          {list.length === 0 && (
            // Chiếm phần trống còn lại của cột để thông báo nằm giữa, không dính sát ô tìm kiếm
            <Stack align="center" justify="center" gap={8} px={16} py={40} ta="center" style={{ flex: 1 }}>
              <span className={classes.addModalEmptyIcon} aria-hidden>
                <IconSearchOff size={26} stroke={1.6} />
              </span>
              <Text fz={16} fw={600} mt={6}>
                Không tìm thấy mẫu nào khớp "{query.trim()}"
              </Text>
              <Text fz={14} maw={420} className={classes.muted}>
                Thử từ khoá khác, ví dụ: sản phẩm, banner, video, tin tức.
              </Text>
              <Button variant="default" mt={8} onClick={() => setQuery("")}>
                Xoá từ khoá
              </Button>
            </Stack>
          )}
        </Stack>

        <Stack
          component="aside"
          aria-label="Thiết lập block mới"
          gap={16}
          p={20}
          className={classes.addModalAside}
        >
          <Box>
            <Text fz={12} fw={600} mb={8} className={classes.muted}>
              Mẫu đã chọn
            </Text>
            <img
              src={designImage(current.thumbId, 720)}
              alt={`Ảnh mẫu ${current.name}`}
              referrerPolicy="no-referrer"
              style={{
                display: "block",
                width: "100%",
                height: "auto",
                border: "1px solid var(--hb-border)",
                borderRadius: 6,
                background: "#fff",
              }}
            />
            <Text fz={16} fw={700} mt={10}>
              {current.name}
            </Text>
            <Text fz={13} mt={4} c="var(--hb-ink-2)">
              {current.desc}
            </Text>
          </Box>
          <TextInput
            label="Tên block"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            description="Tên dùng trong trang quản trị và làm tiêu đề mặc định trên web."
            inputWrapperOrder={["label", "input", "description"]}
          />
          <Select
            label="Vị trí chèn"
            data={positionOptions}
            value={position}
            onChange={setPosition}
            searchable
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
          <Radio.Group
            label="Trạng thái sau khi thêm"
            value={state}
            onChange={setState}
          >
            <Stack gap={8} mt={6}>
              <Radio
                color={ACCENT}
                value="hidden"
                label="Ẩn, bật khi nội dung đã sẵn sàng"
              />
              <Radio
                color={ACCENT}
                value="visible"
                label="Hiển thị ngay sau khi thêm"
              />
            </Stack>
          </Radio.Group>
        </Stack>
      </div>
      {/* Thanh nút luôn hiện ở đáy modal, không bị danh sách mẫu đẩy ra ngoài màn hình */}
      <Group justify="flex-end" gap={8} className={`${classes.page} ${classes.addModalFooter}`}>
        <Button variant="default" onClick={onClose}>
          Huỷ
        </Button>
        <Button variant="filled" color={ACCENT} loading={saving} onClick={handleAdd}>
          Thêm và sửa nội dung
        </Button>
      </Group>
    </Modal>
  );
};

export default AddBlockModal;
